import Link from "next/link";
import Image from "next/image";
import { ChevronRight, ArrowLeft, X } from "lucide-react";
import Icon from "@/components/ui/Icon";
import { cn } from "@/lib/utils";
import { SubmenuItem } from "@/lib/getMenu";
import { memo } from "react";
import { joinNestedPath } from "@/utils";

type NavigationMobileSubmenuItemProps = {
  sub: SubmenuItem;
  index: number;
  parentPath: string;
  openSubIndex: number | null;
  isMenuOpened: boolean;
  toggleSubMenu: (index: number) => void;
  setOpenSubIndex: (index: number | null) => void;
  onMenuClose: () => void;
};

const NavigationMobileSubmenuItem = ({
  sub,
  index,
  parentPath,
  openSubIndex,
  isMenuOpened,
  toggleSubMenu,
  setOpenSubIndex,
  onMenuClose,
  ...rest
}: NavigationMobileSubmenuItemProps) => {
  const isOpen = openSubIndex === index;

  if (sub.nestedMenuItems) {
    return (
      <li {...rest}>
        <button
          onClick={() => toggleSubMenu(index)}
          className={cn(
            "flex justify-between w-full items-center text-base text-gray-800 leading-[137.5%] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded",
            { "py-4": !sub.icon?.url && !sub.description }
          )}
          aria-label={`${isOpen ? 'Close' : 'Open'} ${sub.title} submenu`}
          aria-expanded={isOpen}
          title={`${isOpen ? 'Close' : 'Open'} ${sub.title}`}
        >
          {sub.title}
          <ChevronRight
            className={cn(
              "transform -my-px transition-transform duration-200",
              { "rotate-90": isOpen }
            )}
          />
        </button>

        <div className="fixed inset-0 w-screen h-screen overflow-hidden pointer-events-none">
          <div
            className={cn(
              "absolute inset-y-0 left-full w-screen h-screen bg-white transform transition-transform duration-300 ease-in-out pointer-events-auto flex flex-col translate-x-0",
              {
                "delay-300 duration-0": !isMenuOpened,
                "-translate-x-full": isOpen && isMenuOpened,
              }
            )}
          >
            <div className="py-[14.5px] px-6 border-b border-b-neutral-100 flex items-center justify-between">
              <button
                type="button"
                className="inline-flex items-center text-lg leading-[150%] font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded"
                onClick={() => setOpenSubIndex(null)}
                aria-label={`Back to ${sub.title} menu`}
                title={`Back to ${sub.title} menu`}
              >
                <ArrowLeft width={24} height={24} className="shrink-0 mr-2" />
                <span>{sub.title}</span>
              </button>
              <button
                type="button"
                className="inline-flex w-6 h-6 bg-transparent p-0 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded"
                onClick={onMenuClose}
                aria-label="Close menu"
                title="Close menu"
              >
                <X width={24} height={24} className="w-6 h-6 text-gray-800" />
              </button>
            </div>

            <div className="p-6 flex-auto flex flex-col overflow-y-auto">
              <ul className="grid grid-cols-1 gap-2 auto-rows-fr">
                {sub.nestedMenuItems.map((nested) => (
                  <li key={nested.title}>
                    <Link
                      href={joinNestedPath(parentPath, nested.link)}
                      onClick={onMenuClose}
                      className="flex items-center text-base text-gray-800 leading-[137.5%] py-3"
                    >
                      {!!nested.icon?.url && (
                        <Image
                          src={nested.icon.url}
                          alt="Languex icon"
                          width={16}
                          height={16}
                          className="w-6 h-6 mr-2 shrink-0"
                        />
                      )}
                      <span>{nested.title}</span>
                    </Link>
                  </li>
                ))}
              </ul>

              <div className="w-full mt-2">
                <Link
                  className="inline-flex items-center text-lg leading-[150%] font-semibold py-[4.5px] text-blue"
                  href={parentPath}
                  onClick={onMenuClose}
                >
                  Explore all {sub.title}
                  <Icon
                    id="icon-arrow-right_24"
                    width={24}
                    height={24}
                    className="shrink-0 ml-2"
                  />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </li>
    );
  }

  return (
    <li {...rest}>
      <Link
        href={parentPath}
        onClick={onMenuClose}
        className="flex items-center text-base"
      >
        {!!sub.icon?.url && (
          <span className="inline-flex items-center justify-center w-8 h-8 shrink-0 my-4 mr-2 rounded-full bg-semantic-foreground-soft">
            <Image
              src={sub.icon.url}
              alt="Languex icon"
              width={16}
              height={16}
              className="w-4 h-4"
            />
          </span>
        )}
        <div
          className={cn("grid grid-cols-1", {
            "py-[17px]": !sub.icon?.url && !sub.description,
          })}
        >
          <div className="text-gray-800 text-base leading-[137.5%]">
            {sub.title}
          </div>
          {!!sub.description && (
            <p className="text-sm leading-[130%] text-gray-500">
              {sub.description}
            </p>
          )}
        </div>
      </Link>
    </li>
  );
};

export default memo(NavigationMobileSubmenuItem);
